import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Target, TrendingUp, Award } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function AICareerCoachPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'AI Career Coach - Professional Development & Career Growth | VZMO';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Get personalized AI career coaching for professional development, skill building, and career advancement. 24/7 guidance for your career goals. Start free.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/coach/ai-career-coach');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/coach/ai-career-coach';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('ai-career-coach-page');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            AI Career Coach
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Advance your career with personalized AI coaching. Get guidance on promotions, skill development,
            networking, and professional growth. Available 24/7.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            What Your AI Career Coach Helps With
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <Briefcase className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Career Strategy</h3>
                <p className="text-slate-600">
                  Define your career goals, create advancement plans, and make strategic decisions
                  about opportunities and moves.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Target className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Skill Development</h3>
                <p className="text-slate-600">
                  Identify skill gaps, create learning plans, and track your professional development
                  over time.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <TrendingUp className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Promotions & Raises</h3>
                <p className="text-slate-600">
                  Prepare for promotion conversations, document your achievements, and build
                  compelling cases for advancement.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Award className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Professional Challenges</h3>
                <p className="text-slate-600">
                  Navigate difficult workplace situations, manage conflicts, and develop
                  leadership skills.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Career Coaching Topics</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Preparing for promotion discussions
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Building executive presence
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Networking strategies
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Managing up effectively
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Career pivots and transitions
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Work-life balance strategies
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Who Benefits Most</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Mid-career professionals seeking growth
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                New managers building leadership skills
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Tech workers navigating career ladders
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Remote workers building visibility
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Anyone feeling stuck or plateaued
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Accelerate Your Career</h2>
          <p className="text-lg mb-6 opacity-90">
            Get personalized career coaching at a fraction of traditional costs. Try free for 7 days.
          </p>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Choose AI Career Coaching?</h2>
          <div className="space-y-4 text-slate-600">
            <p>
              Traditional career coaches charge $150-500 per session and require scheduling weeks in advance.
              VZMO gives you unlimited access to career coaching whenever you need it - before a big meeting,
              after a difficult conversation, or when making important career decisions.
            </p>
            <p>
              Your AI career coach provides objective perspectives without workplace politics or bias.
              Get honest feedback and strategic guidance in a completely private, judgment-free environment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
